import type * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";

/**
 * DamageDirection — red arc on the screen edge pointing toward the
 * attacker when the local player takes damage. Fades over 800 ms.
 *
 * Computes the bearing from victim view (camera yaw) to attacker world
 * position and rotates a CSS arc accordingly. 0° = top of screen
 * (camera-forward), 180° = behind, ±90° = sides.
 */
export class DamageDirection {
  private readonly client: GameClient;
  private readonly camera: pc.Entity;
  private readonly root: HTMLDivElement;
  private readonly arc: HTMLDivElement;
  private readonly unsubs: Array<() => void> = [];
  private hideHandle = 0;

  private static readonly LIFETIME_MS = 800;

  constructor(client: GameClient, camera: pc.Entity) {
    this.client = client;
    this.camera = camera;
    this.root = document.createElement("div");
    this.root.id = "damage-direction";
    this.arc = document.createElement("div");
    this.arc.className = "damage-arc";
    this.root.appendChild(this.arc);
    document.body.appendChild(this.root);
    this.injectStyles();

    this.unsubs.push(
      this.client.onHit((msg) => {
        if (msg.victimSessionId !== this.client.sessionId) return;
        if (msg.attackerX === undefined || msg.attackerZ === undefined) return;
        this.show(msg.attackerX, msg.attackerZ);
      }),
    );
  }

  destroy(): void {
    window.clearTimeout(this.hideHandle);
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  private show(attackerX: number, attackerZ: number): void {
    const cam = this.camera.getPosition();
    const dx = attackerX - cam.x;
    const dz = attackerZ - cam.z;
    if (Math.abs(dx) < 1e-3 && Math.abs(dz) < 1e-3) return;

    // Camera yaw in degrees (PlayCanvas: y angle, yaw 0 = facing -Z).
    const camYaw = this.camera.getEulerAngles().y;
    // World bearing from camera to attacker (0° = -Z world, increases
    // counterclockwise looking down). Map to the same convention as
    // camera yaw so the difference is meaningful.
    const worldBearingDeg = (Math.atan2(-dx, -dz) * 180) / Math.PI;
    // Relative bearing in the player's view: 0 = directly ahead,
    // positive = right (clockwise on screen).
    let rel = worldBearingDeg - camYaw;
    while (rel > 180) rel -= 360;
    while (rel < -180) rel += 360;

    // The arc lives at the top of a centered container; rotate to point
    // toward attacker. CSS rotation is clockwise → positive `rel` = arc
    // swings right, matches what the player expects.
    this.arc.style.transform = `translate(-50%, -50%) rotate(${rel}deg)`;
    this.root.style.transition = "opacity 30ms linear";
    this.root.style.opacity = "0.9";
    window.clearTimeout(this.hideHandle);
    this.hideHandle = window.setTimeout(() => {
      this.root.style.transition = `opacity ${DamageDirection.LIFETIME_MS}ms ease-out`;
      this.root.style.opacity = "0";
    }, 80);
  }

  private injectStyles(): void {
    if (document.getElementById("damage-direction-styles")) return;
    const style = document.createElement("style");
    style.id = "damage-direction-styles";
    style.textContent = `
      #damage-direction {
        position: fixed;
        top: 50%;
        left: 50%;
        width: 400px;
        height: 400px;
        transform: translate(-50%, -50%);
        pointer-events: none;
        z-index: 5;
        opacity: 0;
      }
      #damage-direction .damage-arc {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 180px;
        height: 28px;
        margin-top: -200px; /* push to top of container */
        transform: translate(-50%, -50%);
        transform-origin: 50% 200px; /* rotate around container center */
        background:
          radial-gradient(ellipse at center bottom,
            rgba(255, 60, 60, 0.95) 0%,
            rgba(255, 30, 30, 0.55) 45%,
            rgba(255, 0, 0, 0) 85%);
        filter: blur(0.4px);
      }
    `;
    document.head.appendChild(style);
  }
}
